import React, { useMemo } from 'react';
import { useTranslation } from '@tasama/i18n';
import {
  DateInput,
  Dropdown,
  SearchInputSelection,
  InputWithLabel,
  Grid,
  cn,
  CountryFlag,
} from '@tasama/ui';
import {
  getIndustrySectorOptions,
  getEmployeeCountOptions,
  getAnnualRevenueOptions,
  getInvestmentRaisedOptions,
  COMPANY_TYPES,
  type ExtendedCompanyDetailsFormValues,
} from '../../schemas/company-details.schema';
import type { CompanyTypeFormData } from '../../stores/company-type-store';
import {
  getCountriesByCompanyType,
  toAsciiDigits,
  type CountryOption,
} from '@tasama/utils';

interface CompanyDetailsFormProps {
  formData: Partial<ExtendedCompanyDetailsFormValues>;
  errors: Record<string, string>;
  companyTypeData: CompanyTypeFormData;
  onFieldChange: (
    _field: keyof ExtendedCompanyDetailsFormValues,
    _value: string
  ) => void;
  onFieldBlur?: (_field: keyof ExtendedCompanyDetailsFormValues) => void;
  isFieldRequired: (
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => boolean;
  shouldRenderField: (
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => boolean;
  getFieldLabel: (
    _baseLabel: string,
    _fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => string;
  countries: CountryOption[];
  gccCountries: CountryOption[];
  countriesLoading?: boolean | undefined;
  testID?: string | undefined;
  variant?: 'onboarding' | 'edit';
  className?: string;
}

/**
 * Reusable Company Details Form Component
 *
 * A comprehensive form component for company details that adapts based on company type.
 * Supports both onboarding and edit modes with conditional field rendering.
 * Follows Atomic Design principles as an Organism-level component.
 */
export const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({
  formData,
  errors,
  companyTypeData,
  onFieldChange,
  onFieldBlur,
  isFieldRequired,
  shouldRenderField,
  getFieldLabel,
  countries,
  gccCountries,
  countriesLoading = false,
  testID = 'company-details-form',
  className = '',
}) => {
  const { t } = useTranslation('portal');
  const { t: tOnboarding } = useTranslation('onboarding');

  const isInternational =
    companyTypeData.companyType === COMPANY_TYPES.INTERNATIONAL;

  const industrySectorOptions = useMemo(
    () => getIndustrySectorOptions(tOnboarding),
    [tOnboarding]
  );

  const employeeCountOptions = useMemo(() => getEmployeeCountOptions(t), [t]);
  const annualRevenueOptions = useMemo(() => getAnnualRevenueOptions(t), [t]);
  const investmentRaisedOptions = useMemo(
    () => getInvestmentRaisedOptions(t),
    [t]
  );

  const getCountriesList = () =>
    getCountriesByCompanyType(
      isInternational,
      countries ?? [],
      gccCountries ?? []
    ).map((country) => ({
      value: country.value,
      label: country.label,
      icon: <CountryFlag countryCode={country.value} />,
    }));

  return (
    <form
      className={cn('space-y-6', className)}
      noValidate
      data-testid={testID}
    >
      {/* Company Name */}
      <Grid className="grid-cols-1 gap-4">
        <InputWithLabel
          id="companyName"
          label={t('common:companyForm.registeredCompanyName')}
          required
          placeholder={t('common:companyForm.companyNamePlaceholder')}
          value={formData.companyName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('companyName', e.target.value)
          }
          onBlur={() => onFieldBlur?.('companyName')}
          error={!!errors.companyName}
          errorMessage={errors.companyName}
          testID={`${testID}-company-name-field`}
        />
      </Grid>

      {/* Registration Number and Date */}
      <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
        <InputWithLabel
          id="crNumber"
          label={t('common:companyForm.companyRegistrationNumber')}
          required
          placeholder={t('common:companyForm.crNumberPlaceholder')}
          value={formData.crNumber || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('crNumber', toAsciiDigits(e.target.value))
          }
          onBlur={() => onFieldBlur?.('crNumber')}
          error={!!errors.crNumber}
          errorMessage={errors.crNumber}
          testID={`${testID}-registration-number-field`}
        />
        <DateInput
          id="registrationStartDate"
          label={t('common:companyForm.registrationStartDate')}
          required
          value={formData.registrationStartDate || ''}
          onChange={(e: any) =>
            onFieldChange('registrationStartDate', e?.target?.value ?? '')
          }
          onBlur={() => onFieldBlur?.('registrationStartDate')}
          placeholder={t('common:dateInput.placeholder')}
          className={cn(
            formData.registrationStartDate ? 'date-input--has-value' : ''
          )}
          error={!!errors.registrationStartDate}
          errorMessage={errors.registrationStartDate}
          testID={`${testID}-registration-date-field`}
          disabledDays={{ after: new Date() }}
          endMonth={new Date()}
          yearRange={new Date().getFullYear() - 1950}
        />
      </Grid>

      {/* Website */}
      <Grid className="grid-cols-1 gap-4">
        <InputWithLabel
          id="website"
          label={t('common:companyForm.companyWebsite')}
          placeholder={t('common:companyForm.enterWebsiteUrl')}
          value={formData.website || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('website', e.target.value)
          }
          onBlur={() => onFieldBlur?.('website')}
          error={!!errors.website}
          errorMessage={errors.website}
          testID={`${testID}-website-field`}
        />
      </Grid>

      {/* Industry Sector */}
      {shouldRenderField('industrySector') && (
        <Grid className="grid-cols-1 gap-4">
          <Dropdown
            id="industrySector"
            label={t('common:companyForm.industrySector')}
            placeholder={t('common:companyForm.industrySectorPlaceholder')}
            options={industrySectorOptions}
            value={formData.industrySector || ''}
            onChange={(value: string) => onFieldChange('industrySector', value)}
            onBlur={() => onFieldBlur?.('industrySector')}
            error={!!errors.industrySector}
            errorMessage={errors.industrySector}
            testID={`${testID}-industry-sector-field`}
            showLeftIcon={false}
          />
        </Grid>
      )}

      {/* Country of headquarters (International) */}
      {shouldRenderField('countryOfHeadquarter') && (
        <Grid className="grid-cols-1 gap-4">
          <SearchInputSelection
            id="countryOfHeadquarter"
            label={t('companyForm.countryOfHeadquarters')}
            placeholder={t('common:companyForm.countryPlaceholder')}
            options={getCountriesList()}
            value={formData.countryOfHeadquarter || ''}
            onChange={(value: string) =>
              onFieldChange('countryOfHeadquarter', value)
            }
            onBlur={() => onFieldBlur?.('countryOfHeadquarter')}
            error={!!errors.countryOfHeadquarter}
            errorMessage={errors.countryOfHeadquarter}
            testID={`${testID}-country-headquarter-field`}
            showLeftIcon={false}
            disabled={countriesLoading}
          />
        </Grid>
      )}

      {/* Country of Registration (Local) */}
      {shouldRenderField('country') && (
        <Grid className="grid-cols-1 gap-4">
          <SearchInputSelection
            id="country"
            label={t('common:companyForm.countryOfRegistration')}
            placeholder={t('common:companyForm.countryPlaceholder')}
            options={getCountriesList()}
            value={formData.country || ''}
            onChange={(value: string) => onFieldChange('country', value)}
            error={!!errors.country}
            errorMessage={errors.country}
            testID={`${testID}-country-field`}
            showLeftIcon={false}
            disabled
          />
        </Grid>
      )}

      {/* Address */}
      <Grid className="grid-cols-1 gap-4">
        <InputWithLabel
          id="address"
          label={t('common:companyForm.registeredCompanyAddress')}
          placeholder={t('common:companyForm.enterAddress')}
          value={formData.address || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('address', e.target.value)
          }
          onBlur={() => onFieldBlur?.('address')}
          error={!!errors.address}
          errorMessage={errors.address}
          testID={`${testID}-address-field`}
          required={isFieldRequired('address')}
        />
      </Grid>

      {/* City and postalCode */}
      <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
        <InputWithLabel
          id="city"
          label={t('common:companyForm.city')}
          placeholder={t('common:companyForm.enterCity')}
          value={formData.city || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('city', e.target.value)
          }
          onBlur={() => onFieldBlur?.('city')}
          error={!!errors.city}
          errorMessage={errors.city}
          testID={`${testID}-city-field`}
          required={isFieldRequired('city')}
        />
        <InputWithLabel
          id="postalCode"
          label={t('common:companyForm.postcode')}
          placeholder={t('common:companyForm.enterPostcode')}
          value={formData.postalCode || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange('postalCode', toAsciiDigits(e.target.value))
          }
          onBlur={() => onFieldBlur?.('postalCode')}
          error={!!errors.postalCode}
          errorMessage={errors.postalCode}
          testID={`${testID}-postalCode-field`}
          required={isFieldRequired('postalCode')}
        />
      </Grid>

      {/* Employee Count and Annual Revenue */}
      {shouldRenderField('numberOfEmployees') && (
        <Grid
          className={`grid-cols-1 ${shouldRenderField('annualRevenue') ? 'md:grid-cols-2' : ''} gap-4`}
        >
          <Dropdown
            id="numberOfEmployees"
            label={t('common:companyForm.numberOfEmployees')}
            placeholder={t('common:companyForm.numberOfEmployeesPlaceholder')}
            options={employeeCountOptions}
            value={formData.numberOfEmployees || ''}
            onChange={(value: string) =>
              onFieldChange('numberOfEmployees', value)
            }
            onBlur={() => onFieldBlur?.('numberOfEmployees')}
            error={!!errors.numberOfEmployees}
            errorMessage={errors.numberOfEmployees}
            testID={`${testID}-number-of-employees-field`}
            showLeftIcon={false}
            required={isFieldRequired('numberOfEmployees')}
          />
          {shouldRenderField('annualRevenue') && (
            <Dropdown
              id="annualRevenue"
              label={t('common:companyForm.annualRevenue')}
              placeholder={t('common:companyForm.annualRevenuePlaceholder')}
              options={annualRevenueOptions}
              value={formData.annualRevenue || ''}
              onChange={(value: string) =>
                onFieldChange('annualRevenue', value)
              }
              onBlur={() => onFieldBlur?.('annualRevenue')}
              error={!!errors.annualRevenue}
              errorMessage={errors.annualRevenue}
              testID={`${testID}-annual-revenue-field`}
              showLeftIcon={false}
              required={isFieldRequired('annualRevenue')}
            />
          )}
        </Grid>
      )}

      {/* Total Investment Raised */}
      {shouldRenderField('totalInvestmentRaised') && (
        <Grid className="grid-cols-1 gap-4">
          <Dropdown
            id="totalInvestmentRaised"
            label={getFieldLabel(
              t('common:companyForm.totalInvestmentRaised'),
              'totalInvestmentRaised'
            )}
            placeholder={t(
              'common:companyForm.totalInvestmentRaisedPlaceholder'
            )}
            options={investmentRaisedOptions}
            value={formData.totalInvestmentRaised || ''}
            onChange={(value: string) =>
              onFieldChange('totalInvestmentRaised', value)
            }
            onBlur={() => onFieldBlur?.('totalInvestmentRaised')}
            error={!!errors.totalInvestmentRaised}
            errorMessage={errors.totalInvestmentRaised}
            testID={`${testID}-investment-raised-field`}
            showLeftIcon={false}
          />
        </Grid>
      )}
    </form>
  );
};

CompanyDetailsForm.displayName = 'CompanyDetailsForm';

export default CompanyDetailsForm;
